import { TimePicker } from "features/shared/components/TimePicker";
import { rangeTextToSelectedMonths, selectedMonthsToRangeText } from "features/shared/lib/monthRanges";

import { FilterTypes } from "../enums/components";
import { Switch } from "../features/shared/components/Switch";
import { dateToUCTDatetime } from "../utils/date";
import { AutocompleteField } from "./AutocompleteField";
import { DateRangePickerNew } from "./DateRangePickerNew";
import { Dropdown } from "./Dropdown";
import { DropdownSingleSelect } from "./DropdownSingleSelect";
import { ColorInput, Input } from "./Input";
import { NumericRangePicker } from "./NumericRangePicker";

class DynamicFilterBase {
  constructor(args) {
    this.args = args;
    if (!this.args.setNewValue) this.args.setNewValue = (newValue) => args.setConfig(this.args.filterName, newValue);
  }
  createComponent() {}
  filterData(data) {
    return data;
  }
  getComponentProps() {
    const { props } = this.args;
    return props;
  }
  getValue() {
    return this.args.config[this.args.filterName];
  }
}

class DynamicFilterAutocompleteSingle extends DynamicFilterBase {
  constructor(args) {
    super(args);
    this.skipNextBlurCommit = false;
  }

  createComponent() {
    const props = this.getComponentProps();
    const options = props.options ?? [];
    const value = String(this.getValue() ?? "");

    const autocomplete = (
      <AutocompleteField
        {...props}
        freeSolo
        getOptionLabel={(option) => String(option ?? "")}
        inputValue={value}
        onBlur={() => {
          if (this.skipNextBlurCommit) {
            this.skipNextBlurCommit = false;
            return;
          }
          props.onCommit?.(String(this.getValue() ?? ""));
        }}
        onChange={(_, newValue, reason) => {
          const nextValue = typeof newValue === "string" ? newValue : String(newValue ?? "");
          this.args.setNewValue(nextValue);
          if (reason === "selectOption" || reason === "createOption") {
            this.skipNextBlurCommit = true;
            props.onCommit?.(nextValue);
          }
        }}
        onFocus={() => props.onFocus?.(value)}
        onInputChange={(_, newInputValue, reason) => {
          if (reason !== "input") return;
          this.args.setNewValue(newInputValue);
        }}
        options={options}
        value={value}
      />
    );

    return autocomplete;
  }

  filterData(data) {
    return data;
  }
}

class DynamicFilterColorInput extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }

  createComponent() {
    return (
      <ColorInput
        className="block w-3/4 "
        color={this.getValue()}
        setColor={(color) => this.args.setNewValue(color)}
        {...this.getComponentProps()}
      />
    );
  }
  filterData(data) {
    return data;
  }
}

class DynamicFilterDateRangePicker extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }
  createComponent() {
    return (
      <DateRangePickerNew onChange={this.args.setNewValue} value={this.getValue()} {...this.getComponentProps()} />
    );
  }
  filterData(data, dataKey) {
    const dates = this.getValue();
    if (!dates) return data;

    const dataDateIsString = data?.some((row) => typeof row[dataKey] === "string");
    const dataDateIsStringWithoutTime = dataDateIsString && data?.some((row) => row[dataKey]?.length === 10);

    const minDate = dateToUCTDatetime(dates[0]);
    let maxDate = dates[1];
    maxDate.setDate(maxDate.getDate() + 1);
    const dayAfterMaxDate = dateToUCTDatetime(maxDate);
    return data?.filter((row) => {
      const datetimeString = row[dataKey] + (dataDateIsStringWithoutTime ? "T00:00:00" : "");
      const datetime = dataDateIsString ? new Date(datetimeString) : row[dataKey];

      const isUpperThanMin = !dates[0] || datetime >= minDate;
      const isLowerThanMax = !dates[1] || datetime < dayAfterMaxDate;
      return isUpperThanMin && isLowerThanMax;
    });
  }
  getValue() {
    const selectedDates = super.getValue();
    const startDate = selectedDates?.[0] || this.args.props.minDate;
    const endDate = selectedDates?.[1] || this.args.props.maxDate;
    return [new Date(startDate), new Date(endDate)];
  }
}

class DynamicFilterDropdownMulti extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }
  createComponent() {
    return <Dropdown setValues={this.args.setNewValue} values={this.getValue() ?? []} {...this.getComponentProps()} />;
  }
  filterData(data, dataKey) {
    const selectedValues = this.getValue();
    if (this.args.filterFunction) return this.args.filterFunction(data, dataKey, selectedValues);
    return data?.filter((row) => !selectedValues?.length || selectedValues.includes(row[dataKey]));
  }
}

class DynamicFilterDropdownSingle extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }

  createComponent() {
    return (
      <DropdownSingleSelect
        selectedValue={this.getValue()}
        setSelectedValue={this.args.setNewValue}
        {...this.getComponentProps()}
      />
    );
  }
  filterData(data, dataKey) {
    return data?.filter((row) => row[dataKey] === this.getValue() || this.getValue() === undefined);
  }
}

class DynamicFilterInput extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }

  createComponent() {
    return (
      <Input
        onChange={(e) => this.args.setNewValue(e.target.value)}
        value={this.getValue()}
        {...this.getComponentProps()}
      />
    );
  }
  filterData(data) {
    return data;
  }
}

class DynamicFilterMonthRangesMulti extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }

  createComponent() {
    const rangeText = this.getValue() ?? "";
    const selectedMonths = rangeTextToSelectedMonths(rangeText).map(String);

    return (
      <Dropdown
        selectProps={{
          renderValue: () => rangeText,
        }}
        setValues={(months) => this.args.setNewValue(selectedMonthsToRangeText(months.map((month) => Number(month))))}
        values={selectedMonths}
        {...this.getComponentProps()}
      />
    );
  }

  filterData(data) {
    return data;
  }
}

class DynamicFilterNumericRangePicker extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }
  createComponent() {
    return (
      <NumericRangePicker setValue={this.args.setNewValue} value={this.getValue()} {...this.getComponentProps()} />
    );
  }

  filterData(data, dataKey) {
    const value = this.getValue();
    if (this.args.filterFunction) return this.args.filterFunction(data, dataKey, value);
    if (value?.[0] == undefined || value?.[1] == undefined) return data;
    return data?.filter((row) => {
      return value[0] <= row[dataKey] && row[dataKey] <= value[1];
    });
  }
}

class DynamicFilterSwitch extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }

  createComponent() {
    const { className, label } = this.getComponentProps();

    return (
      <Switch
        checked={Boolean(this.getValue())}
        className={className}
        label={label}
        onChange={(checked) => this.args.setNewValue(checked)}
      />
    );
  }

  filterData(data, dataKey) {
    const value = this.getValue();
    if (value === undefined || value === null) return data;
    return data?.filter((row) => Boolean(row[dataKey]) === Boolean(value));
  }
}

class DynamicFilterTimePicker extends DynamicFilterBase {
  constructor(args) {
    super(args);
  }

  createComponent() {
    return (
      <TimePicker
        onChange={(value) => this.args.setNewValue(value)}
        value={this.getValue()}
        {...this.getComponentProps()}
      />
    );
  }
}

export class DynamicFilterFactory {
  constructor(filterType, args) {
    this.filterType = filterType;
    this.args = args;
  }

  createFilterClassInstance() {
    if (this.filterType === FilterTypes.AUTOCOMPLETE_SINGLE) return new DynamicFilterAutocompleteSingle(this.args);
    if (this.filterType === FilterTypes.DROPDOWN_SINGLE) return new DynamicFilterDropdownSingle(this.args);
    if (this.filterType === FilterTypes.DROPDOWN_MULTI) return new DynamicFilterDropdownMulti(this.args);
    if (this.filterType === FilterTypes.MONTH_RANGES_MULTI) return new DynamicFilterMonthRangesMulti(this.args);
    if (this.filterType === FilterTypes.DATERANGE_PICKER) return new DynamicFilterDateRangePicker(this.args);
    if (this.filterType === FilterTypes.INPUT) return new DynamicFilterInput(this.args);
    if (this.filterType === FilterTypes.COLOR_INPUT) return new DynamicFilterColorInput(this.args);
    if (this.filterType === FilterTypes.NUMERIC_RANGE_PICKER) return new DynamicFilterNumericRangePicker(this.args);
    if (this.filterType === FilterTypes.SWITCH) return new DynamicFilterSwitch(this.args);
    if (this.filterType === FilterTypes.TIME_PICKER) return new DynamicFilterTimePicker(this.args);
  }
}
